import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messageService } from '../services/message.service';
import { Message, SendMessageRequest } from '../types/message.types';
import { useToast } from './useToast';
import { ApiError } from '../services/api';

export function useMessages(projectId: string | undefined) {
  return useQuery({
    queryKey: ['messages', projectId],
    queryFn: () => messageService.getMessages(projectId!),
    enabled: !!projectId,
  });
}

export function useSendMessage(projectId: string) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: SendMessageRequest) => messageService.sendMessage(projectId, data),

    // Optimistically append the user message so the UI feels instant.
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['messages', projectId] });
      const previous = queryClient.getQueryData<Message[]>(['messages', projectId]);
      const optimistic: Message = {
        id: `optimistic-${Date.now()}`,
        projectId,
        role: 'user',
        content: data.content,
        createdAt: new Date().toISOString(),
      };
      queryClient.setQueryData<Message[]>(['messages', projectId], (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous };
    },

    // Replace the optimistic entry with the real messages returned by the server,
    // then invalidate so the cache syncs fully in the background.
    onSuccess: (data) => {
      queryClient.setQueryData<Message[]>(['messages', projectId], (old) => {
        const filtered = (old ?? []).filter((m) => !m.id.startsWith('optimistic-'));
        return [...filtered, data.userMessage, data.assistantMessage];
      });
      queryClient.invalidateQueries({ queryKey: ['messages', projectId] });
    },

    // On error: keep the user message (it's persisted server-side).
    // Only roll back for rate-limit / payment errors where the message wasn't saved.
    onError: (err, _vars, context) => {
      if (err instanceof ApiError && (err.status === 402 || err.status === 429)) {
        // These errors happen before save — roll back optimistic update.
        if (context?.previous) {
          queryClient.setQueryData(['messages', projectId], context.previous);
        }
        return;
      }
      // For other errors the user message is already persisted.
      // Invalidate to fetch the real message from the server.
      queryClient.invalidateQueries({ queryKey: ['messages', projectId] });
      showToast('Failed to generate. Please try again.', 'error');
    },
  });
}
